import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useSEO } from '../hooks/useSEO';
import { useNavigate } from 'react-router-dom';
import { LogOut, Menu, Briefcase, CheckCircle, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import LanguageSelector from '../components/LanguageSelector';
import ThemeToggle from '../components/ThemeToggle';
import MobileBottomNav from '../components/MobileBottomNav';
import MobileMenu from '../components/MobileMenu';
import SiteFooter from '../components/SiteFooter';
import ToolSidebar from '../components/ToolSidebar';
import SeoMainImage from '../components/SeoMainImage';
import HreflangTags from '../components/HreflangTags';
import StepByStepBookingModal from '../components/StepByStepBookingModal';
import '../styles/FreelanceVisaPage.css';
import '../styles/HomePage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_DARK = "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png";

// Fallback used if the API is unreachable. Real data is fetched from /api/services
// so admin edits in the Admin Panel flow through to this page automatically.
const FREELANCE_FALLBACK = {
  id: '4', name: 'Freelance Visa', subtitle: 'Freelance Visa',
  duration: '5-7 Days', price: 'AED 5,000',
  description: 'Get your freelance visa', icon: 'Briefcase', position: 2
};

const FreelanceVisaPage = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const currentLogo = theme === 'light' ? LOGO_LIGHT : LOGO_DARK;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [freelanceService, setFreelanceService] = useState(FREELANCE_FALLBACK);

  // Fetch the live Freelance Visa service from the DB so admin description
  // edits in the Admin Panel are reflected on this page immediately.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/services`);
        const list = Array.isArray(resp.data) ? resp.data : [];
        const match = list.find((s) =>
          (s.name || '').toLowerCase().includes('freelance')
        );
        if (match && !cancelled) setFreelanceService(match);
      } catch (e) {
        // Silent — keep fallback. Network failures shouldn't break the page.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pick the right description for the current language with sensible fallback chain.
  const liveDescription = (() => {
    if (language === 'ru' && freelanceService.description_ru) return freelanceService.description_ru;
    if (language === 'uz' && freelanceService.description_uz) return freelanceService.description_uz;
    return freelanceService.description || t('fvPageDesc') || 'Start your freelance career in Dubai with a fully legal UAE freelance visa from Registan Tourism.';
  })();

  // Live price/duration with translation fallbacks for backwards compat
  const livePrice = freelanceService.price ? `From ${freelanceService.price}` : (t('fvPrice') || 'From AED 5,000');
  const liveDuration = freelanceService.duration ? `Processing: ${freelanceService.duration}` : (t('fvDuration') || 'Processing: 5-7 Days');

  const seoDefaults = {
    title: 'Dubai Freelance Visa 2026 — Apply Online for UAE Freelancer Permit | Registan Tourism',
    description: 'Get your Dubai freelance visa in 5-7 days. Registan Tourism provides complete freelance permit services in the UAE including trade license, visa stamping, Emirates ID, and medical. Apply online now from AED 5,000.',
    keywords: 'freelance visa Dubai, UAE freelancer permit, Dubai freelance visa 2026, apply freelance visa online, UAE freelance license, Registan Tourism freelance visa, Dubai work permit freelancer'
  };
  const seo = useSEO('freelance-visa', seoDefaults);

  return (
    <div className="freelance-visa-page" data-testid="freelance-visa-page">
      <Helmet>
        <title>{seo?.title || seoDefaults.title}</title>
        <meta name="description" content={seo?.description || seoDefaults.description} />
        <meta name="keywords" content={seo?.keywords || seoDefaults.keywords} />
        <link rel="canonical" href="https://registantours.com/freelance-visa" />
        <meta property="og:title" content={seo?.title || seoDefaults.title} />
        <meta property="og:description" content={seo?.description || seoDefaults.description} />
        <meta property="og:url" content="https://registantours.com/freelance-visa" />
        <meta property="og:type" content="website" />
      </Helmet>

      <header className="dark-header">
        <div className="header-left">
          <div className="header-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src={currentLogo} alt="Registan Tourism" className="logo-img" />
          </div>
          <LanguageSelector />
          <ThemeToggle />
        </div>
        {isAuthenticated ? (
          <div className="header-user-menu">
            <button className="btn-user-profile" onClick={() => navigate('/dashboard')}>
              <div className="user-avatar-small">{user?.name?.charAt(0)?.toUpperCase()}</div>
              <span>{user?.name}</span>
            </button>
            <button className="btn-logout-header" onClick={logout} title="Logout">
              <LogOut size={20} />
            </button>
          </div>
        ) : (
          <div className="header-auth-buttons">
            <button className="btn-secondary btn-login" onClick={() => navigate('/login')}>{t('login') || 'Login'}</button>
            <button className="btn-primary btn-signup" onClick={() => navigate('/register')}>{t('signUp') || 'SIGN UP'}</button>
          </div>
        )}
        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(true)}>
          <Menu size={24} />
        </button>
      </header>
      <SeoMainImage seo={seo} />
      <HreflangTags path="/freelance-visa" />

      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />

      <div className="tool-page-layout">
        <ToolSidebar />
        <main className="freelance-visa-main tool-page-content">

          <div className="fv-hero">
            <div className="fv-hero-icon"><Briefcase size={36} /></div>
            <h1 data-testid="freelance-visa-h1">{t('fvPageH1') || 'Dubai Freelance Visa 2026 — Work Independently in the UAE with Full Legal Residency'}</h1>
            <h2 data-testid="freelance-visa-h2">{t('fvPageH2') || 'Apply Online for Your UAE Freelancer Permit — Fast Processing, Complete Support from Registan Tourism'}</h2>
            <p className="fv-hero-desc" data-testid="freelance-visa-desc">
              {liveDescription}
            </p>
          </div>

          <div className="fv-cta-section">
            <button
              className="fv-apply-btn"
              onClick={() => setIsBookingOpen(true)}
              data-testid="freelance-visa-apply-btn"
            >
              <Briefcase size={20} />
              {t('fvApplyBtn') || 'Apply for Freelance Visa Now'}
              <ArrowRight size={18} />
            </button>
            <div className="fv-meta">
              <span className="fv-price">{livePrice}</span>
              <span className="fv-duration">{liveDuration}</span>
            </div>
          </div>

          <div className="fv-features">
            <h3 className="fv-features-title">{t('fvFeaturesTitle') || "What's Included"}</h3>
            <div className="fv-features-grid">
              {[
                t('fvFeature1') || 'Freelance Trade License',
                t('fvFeature2') || 'UAE Residence Visa Stamping',
                t('fvFeature3') || 'Emirates ID Processing',
                t('fvFeature4') || 'Medical Fitness Test',
                t('fvFeature5') || 'Document Pickup & Delivery',
                t('fvFeature6') || 'Real-Time Status Tracking',
                t('fvFeature7') || 'Dedicated Support Manager',
                t('fvFeature8') || 'No Hidden Fees'
              ].map((item, idx) => (
                <div key={idx} className="fv-feature-item">
                  <CheckCircle size={18} className="fv-check" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

        </main>
      </div>

      {isBookingOpen && (
        <StepByStepBookingModal
          onClose={() => setIsBookingOpen(false)}
          selectedService={freelanceService}
        />
      )}

      <SiteFooter />
      <MobileBottomNav />
    </div>
  );
};

export default FreelanceVisaPage;
